import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PortalHome() {
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/trip").then((r) => { setTrip(r.data.trip); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 md:px-14 py-14 md:py-20 max-w-5xl">
      <p className="eyebrow">Welcome back</p>
      <h1 className="mt-4 font-serif text-4xl md:text-6xl text-jade font-light leading-[1.05]">
        Good day, <span className="italic">{user?.name?.split(" ").slice(-1)[0]}.</span>
      </h1>

      {loading ? (
        <p className="mt-12 eyebrow text-ink/50">Loading your journey…</p>
      ) : !trip ? (
        <div data-testid="portal-no-trip" className="mt-16 bg-sand p-10">
          <p className="eyebrow text-copper">No upcoming journey yet</p>
          <h2 className="mt-4 font-serif text-3xl text-jade font-light">Your portal is ready.</h2>
          <p className="mt-4 text-ink/75 text-[15px] font-light leading-relaxed max-w-xl">
            Once a member of our concierge confirms your enquiry, your full itinerary, documents, and private guide will appear here.
          </p>
          <Link to="/contact" className="btn-primary mt-8 inline-flex">Start an Enquiry</Link>
        </div>
      ) : (
        <>
          <section data-testid="portal-trip-overview" className="mt-12 bg-sand p-8 md:p-12">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] tracking-[0.2em] uppercase border border-jade px-3 py-1 text-jade">{trip.status}</span>
              <span className="text-[12px] tracking-[0.16em] uppercase text-ink/60">{trip.start_date} → {trip.end_date}</span>
              <span className="text-[12px] tracking-[0.16em] uppercase text-ink/60">· {trip.travellers} travellers</span>
            </div>
            <h2 className="mt-6 font-serif text-3xl md:text-5xl text-jade font-light leading-tight">{trip.title}</h2>
            <p className="mt-4 text-ink/70 text-[15px] font-light">
              {trip.destinations?.join(" · ")}
            </p>
            <Link to="/portal/itinerary" className="btn-primary mt-8 inline-flex">View Day-by-Day Itinerary</Link>
          </section>

          <section className="mt-14 grid md:grid-cols-3 gap-6">
            <Link to="/portal/itinerary" data-testid="portal-card-itinerary" className="bg-ivory border border-mist/60 p-7 hover:border-jade transition-colors">
              <p className="eyebrow">Itinerary</p>
              <p className="mt-3 font-serif text-2xl text-jade">{trip.itinerary?.length || trip.duration_days || 0} days</p>
              <p className="mt-2 text-ink/65 text-[14px] font-light">Day-by-day schedule</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-jade">View <ArrowRight strokeWidth={1} className="w-3.5 h-3.5" /></span>
            </Link>
            <Link to="/portal/documents" data-testid="portal-card-documents" className="bg-ivory border border-mist/60 p-7 hover:border-jade transition-colors">
              <p className="eyebrow">Documents</p>
              <p className="mt-3 font-serif text-2xl text-jade">{trip.documents?.length || 0} files</p>
              <p className="mt-2 text-ink/65 text-[14px] font-light">Vouchers, visas & more</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-jade">View <ArrowRight strokeWidth={1} className="w-3.5 h-3.5" /></span>
            </Link>
            <Link to="/portal/messages" data-testid="portal-card-messages" className="bg-ivory border border-mist/60 p-7 hover:border-jade transition-colors">
              <p className="eyebrow">Concierge</p>
              <p className="mt-3 font-serif text-2xl text-jade">In touch</p>
              <p className="mt-2 text-ink/65 text-[14px] font-light">Message your concierge</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-jade">Open <ArrowRight strokeWidth={1} className="w-3.5 h-3.5" /></span>
            </Link>
          </section>

          {trip.guide && (
            <section data-testid="portal-guide" className="mt-14 grid md:grid-cols-12 gap-8 bg-ivory border border-mist/60 p-8 md:p-10">
              <div className="md:col-span-3">
                <img src={trip.guide.photo} alt={trip.guide.name} className="w-full aspect-square object-cover" />
              </div>
              <div className="md:col-span-9">
                <p className="eyebrow">Your private guide</p>
                <h3 className="mt-3 font-serif text-3xl text-jade font-light">{trip.guide.name}</h3>
                <p className="text-ink/60 text-[13px] mt-1">{trip.guide.role}</p>
                <p className="mt-4 text-ink/75 text-[15px] font-light leading-relaxed max-w-xl">{trip.guide.bio}</p>
                <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-[13px] text-ink/75">
                  <span><span className="eyebrow mr-2">Tel</span>{trip.guide.phone}</span>
                  <span><span className="eyebrow mr-2">Email</span>{trip.guide.email}</span>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
